import React, { useState } from 'react';
import Icon from '../ui/Icon';
import { scoreColor } from './diagnosticHelpers';
import './DiagnosticIntelligenceSection.css';

// Presentation-only state → label/color. Never decides a topic's state —
// only how an already-decided state is drawn. The backend
// (topicIntelligenceService) is the sole source of `state`.
const STATE_META = {
  mastered:          { label: 'Mastered',        color: '#059669', bg: 'rgba(5,150,105,0.10)' },
  strong:            { label: 'Strong',          color: '#0891b2', bg: 'rgba(8,145,178,0.10)' },
  improving:         { label: 'Improving',       color: '#7c3aed', bg: 'rgba(124,58,237,0.10)' },
  needs_attention:   { label: 'Needs Attention', color: '#dc2626', bg: 'rgba(220,38,38,0.09)' },
  declining:         { label: 'Declining',       color: '#e11d48', bg: 'rgba(225,29,72,0.10)' },
  insufficient_data: { label: 'Not Enough Data', color: '#94a3b8', bg: '#f1f5f9' },
};

const TREND_ICON = { improving: 'trend-up', declining: 'trend-down', stable: 'trend-flat' };

const TopicRow = ({ topic, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const meta = STATE_META[topic.state] || STATE_META.insufficient_data;
  const acc = topic.evidence?.accuracy_confidence_adjusted_pct;

  return (
    <div className="dxi-topic-row-wrap">
      <button type="button" className="dxi-topic-row" onClick={() => setOpen(v => !v)}>
        <span className="dxi-topic-name">{topic.topic_name}</span>
        <span className="dxi-topic-cat">{topic.category}</span>
        {topic.trend?.label && topic.trend.label !== 'insufficient_data' && (
          <span className="dxi-topic-trend" style={{ color: topic.trend.label === 'declining' ? '#e11d48' : topic.trend.label === 'improving' ? '#7c3aed' : '#94a3b8' }}>
            {TREND_ICON[topic.trend.label] && <Icon name={TREND_ICON[topic.trend.label]} size={16} title={topic.trend.label} />}
          </span>
        )}
        {acc != null && <span className="dxi-topic-acc" style={{ color: scoreColor(acc) }}>{Math.round(acc)}%</span>}
        <span className="dxi-topic-pill" style={{ color: meta.color, background: meta.bg }}>{meta.label}</span>
        <span className="dxi-topic-chevron"><Icon name={open ? 'chevron-up' : 'chevron-down'} size={16} /></span>
      </button>
      {open && (
        <div className="dxi-topic-detail">
          <p>{topic.explanation}</p>
          {topic.speed?.is_slow && <p className="dxi-topic-note"><Icon name="timer" size={14} /> Solve time is above the expected pace for this topic.</p>}
          {topic.mistakes?.repeated_unresolved > 0 && <p className="dxi-topic-note"><Icon name="refresh" size={14} /> {topic.mistakes.repeated_unresolved} recurring, unresolved mistake{topic.mistakes.repeated_unresolved > 1 ? 's' : ''}.</p>}
        </div>
      )}
    </div>
  );
};

// `data` = the full response from GET /portal/diagnostic-intelligence
// ({ topics, weak_areas, sections, mistakes }) — nothing here is recalculated.
const DiagnosticIntelligenceSection = ({ data }) => {
  const [showAll, setShowAll] = useState(false);
  if (!data) return null;

  const { topics = [], weak_areas = [], sections = [], mistakes } = data;
  const withEvidence = topics.filter(t => t.state !== 'insufficient_data');
  const noEvidenceCount = topics.length - withEvidence.length;

  // Weak areas first (already ranked by the backend), then everything else in
  // whatever order the API returned it.
  const weakIds = new Set(weak_areas.map(w => w.topic_id));
  const ordered = [
    ...weak_areas,
    ...withEvidence.filter(t => !weakIds.has(t.topic_id)),
  ];
  const visible = showAll ? ordered : ordered.slice(0, 6);

  if (topics.length === 0) {
    return (
      <section className="dxi-section">
        <div className="dxi-section-head">
          <h3 className="dxi-section-title">Your Diagnostic Breakdown</h3>
        </div>
        <div className="dxi-empty">
          <span className="dxi-empty-icon"><Icon name="chart" size={40} /></span>
          <p>No topic-level evidence yet. Take an aptitude or diagnostic test to start building your breakdown.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="dxi-section">
      <div className="dxi-section-head">
        <h3 className="dxi-section-title">Your Diagnostic Breakdown</h3>
        <p className="dxi-section-desc">What's happening in each topic — separate from what to do about it</p>
      </div>

      {sections.length > 1 && (
        <div className="dxi-card dxi-sections-card">
          {sections.filter(s => s.avg_accuracy_pct != null).map(s => (
            <div className="dxi-section-row" key={s.category}>
              <span className="dxi-section-cat">{s.category}</span>
              <div className="dxi-section-track"><div className="dxi-section-fill" style={{ width: `${s.avg_accuracy_pct}%`, background: scoreColor(s.avg_accuracy_pct) }} /></div>
              <span className="dxi-section-val" style={{ color: scoreColor(s.avg_accuracy_pct) }}>{s.avg_accuracy_pct}%</span>
            </div>
          ))}
        </div>
      )}

      <div className="dxi-card">
        {visible.map((t, i) => <TopicRow key={t.topic_id} topic={t} defaultOpen={i === 0 && weakIds.has(t.topic_id)} />)}
        {ordered.length > 6 && (
          <button type="button" className="dxi-show-more" onClick={() => setShowAll(v => !v)}>
            {showAll ? 'Show less' : `Show ${ordered.length - 6} more topics`}
          </button>
        )}
        {noEvidenceCount > 0 && (
          <p className="dxi-muted-note">{noEvidenceCount} more topic{noEvidenceCount > 1 ? 's' : ''} with not enough evidence yet to assess.</p>
        )}
      </div>

      {mistakes && (mistakes.total > 0) && (
        <div className="dxi-card dxi-mistakes-card">
          <div className="dxi-mistakes-stats">
            <div><b>{mistakes.resolved_count}</b> corrected</div>
            <div><b>{mistakes.recurring_count}</b> recurring</div>
            <div><b>{mistakes.recent_unresolved_count}</b> recent</div>
          </div>
          {mistakes.recently_corrected_items?.length > 0 && (
            <p className="dxi-mistake-positive"><Icon name="check" size={14} /> You've improved on {mistakes.recently_corrected_items.map(m => m.topic_name).slice(0, 2).join(', ')}{mistakes.recently_corrected_items.length > 2 ? ' and more' : ''} — nice work.</p>
          )}
          {mistakes.recurring_count > 0 && (
            <p className="dxi-mistake-warning">You have {mistakes.recurring_count} mistake{mistakes.recurring_count > 1 ? 's' : ''} you keep repeating — worth a review.</p>
          )}
        </div>
      )}
    </section>
  );
};

export default DiagnosticIntelligenceSection;
